/**
 * Super Admin - Users Management
 * Dedicated page for managing system users
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

function UsersManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Filter states
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Tab: 0 = Staff (User table), 1 = Clients (Client table)
  const [tabValue, setTabValue] = useState(0);
  const [clientsData, setClientsData] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientStatusFilter, setClientStatusFilter] = useState("all");
  const [clientEmailVerifiedFilter, setClientEmailVerifiedFilter] = useState("all");

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (tabValue === 1) {
      fetchClients();
    }
  }, [tabValue, clientStatusFilter, clientEmailVerifiedFilter]);

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const filters = { limit: 1000 };
      if (clientStatusFilter !== "all") {
        filters.isActive = clientStatusFilter === "active";
      }
      if (clientEmailVerifiedFilter !== "all") {
        filters.emailVerified = clientEmailVerifiedFilter === "verified";
      }
      const response = await superAdminAPI.getAllClients(filters);
      setClientsData(response.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setMessage({
        type: "error",
        text: "Failed to load clients. Please try again.",
      });
      setClientsData([]);
    } finally {
      setClientsLoading(false);
    }
  };

  // Filter data when filters or data change
  useEffect(() => {
    let filtered = [...data];

    // Filter by role
    if (roleFilter !== "all") {
      filtered = filtered.filter((user) => user.role?.name === roleFilter);
    }

    // Filter by status
    if (statusFilter !== "all") {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((user) => (user.isActive !== false) === isActive);
    }

    setFilteredData(filtered);
  }, [data, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getAllUsers();
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage({
        type: "error",
        text: "Failed to load users. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await superAdminAPI.getAvailableRoles();
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await superAdminAPI.deactivateUserAccount(userId);
      if (response.success) {
        setMessage({
          type: "success",
          text: "User deactivated successfully.",
        });
        await fetchUsers();
        setActionDialog({ open: false, action: null });
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to deactivate user. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (userId) => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await superAdminAPI.activateUserAccount(userId);
      if (response.success) {
        setMessage({
          type: "success",
          text: "User activated successfully.",
        });
        await fetchUsers();
        setActionDialog({ open: false, action: null });
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Error activating user:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to activate user. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const response = await superAdminAPI.deleteUserAccount(userId);
      if (response.success) {
        setMessage({
          type: "success",
          text: "User deleted successfully.",
        });
        await fetchUsers();
        setActionDialog({ open: false, action: null });
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to delete user. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openMenu = (event, user) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuAnchorPosition({ top: rect.bottom, left: rect.right });
      setSelectedUser(user);
    }
  };

  const closeMenu = () => {
    setMenuAnchorPosition(null);
    setSelectedUser(null);
  };

  const openActionDialog = (action) => {
    setActionDialog({ open: true, action });
    setMenuAnchorPosition(null); // close menu visually but keep selectedUser for confirmAction
  };

  const confirmAction = () => {
    if (!selectedUser) return;

    const userId = String(selectedUser._id || selectedUser.id);

    if (actionDialog.action === "deactivate") {
      handleDeactivate(userId);
    } else if (actionDialog.action === "activate") {
      handleActivate(userId);
    } else if (actionDialog.action === "delete") {
      handleDelete(userId);
    }
  };

  const columns = [
    { Header: "Name", accessor: "fullName", width: "25%" },
    { Header: "Email", accessor: "email", width: "25%" },
    {
      Header: "Role",
      accessor: "role",
      width: "20%",
      Cell: ({ value }) => <Chip label={value || "N/A"} color="info" size="small" />,
    },
    {
      Header: "Status",
      accessor: "isActive",
      width: "15%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "10%",
      Cell: ({ row }) => {
        const userId = row.original._id || row.original.id;

        const handleClick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          openMenu(e, row.original);
        };

        return (
          <MDBox display="flex" justifyContent="flex-end" width="100%">
            <IconButton onClick={handleClick} size="small" aria-label="user actions">
              <Icon>more_vert</Icon>
            </IconButton>
          </MDBox>
        );
      },
    },
  ];

  const handleClearFilters = () => {
    setRoleFilter("all");
    setStatusFilter("all");
  };

  const hasActiveFilters = roleFilter !== "all" || statusFilter !== "all";
  const hasActiveClientFilters =
    clientStatusFilter !== "all" || clientEmailVerifiedFilter !== "all";

  const clientColumns = [
    { Header: "Name", accessor: "fullName", width: "30%" },
    { Header: "Email", accessor: "email", width: "35%" },
    {
      Header: "Status",
      accessor: "isActive",
      width: "15%",
      Cell: ({ value }) => (
        <Chip
          label={value !== false ? "Active" : "Inactive"}
          color={value !== false ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      Header: "Email Verified",
      accessor: "emailVerified",
      width: "20%",
      Cell: ({ value }) => (
        <Chip
          label={value ? "Verified" : "Not Verified"}
          color={value ? "success" : "warning"}
          size="small"
        />
      ),
    },
  ];

  const rows = filteredData.map((user) => ({
    fullName: user.fullName,
    email: user.email,
    role: user.role?.name || "N/A",
    isActive: user.isActive !== false,
    emailVerified: user.emailVerified || false,
    _id: user._id || user.id,
  }));

  const clientRows = clientsData.map((client) => ({
    fullName: client.fullName || "—",
    email: client.email || "—",
    isActive: client.isActive !== false,
    emailVerified: !!client.emailVerified,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Users & Clients
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {tabValue === 0
                ? "Manage staff user accounts (roles and status)"
                : "View website clients (booking accounts from the client table)"}
            </MDTypography>
          </MDBox>
          {tabValue === 0 && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={() => navigate("/super-admin/users/create")}
              startIcon={<Icon>add</Icon>}
            >
              Create User
            </MDButton>
          )}
        </MDBox>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Staff" icon={<Icon fontSize="small">badge</Icon>} iconPosition="start" />
            <Tab label="Clients" icon={<Icon fontSize="small">people</Icon>} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Message Alert */}
        {message.text && (
          <MDBox mb={3}>
            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          </MDBox>
        )}

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Filters
              </MDTypography>
              {tabValue === 0 && hasActiveFilters && (
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<Icon>clear</Icon>}
                >
                  Clear Filters
                </MDButton>
              )}
              {tabValue === 1 && hasActiveClientFilters && (
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    setClientStatusFilter("all");
                    setClientEmailVerifiedFilter("all");
                  }}
                  startIcon={<Icon>clear</Icon>}
                >
                  Clear Filters
                </MDButton>
              )}
            </MDBox>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              {tabValue === 0 && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="role-filter-label">Role</InputLabel>
                      <Select
                        labelId="role-filter-label"
                        id="role-filter"
                        value={roleFilter}
                        label="Role"
                        onChange={(e) => setRoleFilter(e.target.value)}
                        sx={{
                          height: "56px",
                          "& .MuiSelect-select": {
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <MenuItem value="all">
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>all_inclusive</Icon>
                            All Roles
                          </MDBox>
                        </MenuItem>
                        {roles
                          .filter((role) => (role.name || "").toLowerCase() !== "client")
                          .map((role) => (
                            <MenuItem key={role._id || role.id} value={role.name}>
                              <MDBox display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>badge</Icon>
                                {role.name}
                              </MDBox>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        id="status-filter"
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{
                          height: "56px",
                          "& .MuiSelect-select": {
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <MenuItem value="all">
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ mr: 1, fontSize: "1.2rem" }}>all_inclusive</Icon>
                            All Status
                          </MDBox>
                        </MenuItem>
                        <MenuItem value="active">
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ mr: 1, fontSize: "1.2rem", color: "success.main" }}>
                              check_circle
                            </Icon>
                            Active
                          </MDBox>
                        </MenuItem>
                        <MenuItem value="inactive">
                          <MDBox display="flex" alignItems="center">
                            <Icon sx={{ mr: 1, fontSize: "1.2rem", color: "error.main" }}>
                              block
                            </Icon>
                            Inactive
                          </MDBox>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              {tabValue === 1 && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="client-status-filter-label">Status</InputLabel>
                      <Select
                        labelId="client-status-filter-label"
                        value={clientStatusFilter}
                        label="Status"
                        onChange={(e) => setClientStatusFilter(e.target.value)}
                        sx={{
                          height: "56px",
                          "& .MuiSelect-select": {
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel id="client-email-verified-label">Email Verified</InputLabel>
                      <Select
                        labelId="client-email-verified-label"
                        value={clientEmailVerifiedFilter}
                        label="Email Verified"
                        onChange={(e) => setClientEmailVerifiedFilter(e.target.value)}
                        sx={{
                          height: "56px",
                          "& .MuiSelect-select": {
                            height: "56px",
                            display: "flex",
                            alignItems: "center",
                          },
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="verified">Verified</MenuItem>
                        <MenuItem value="unverified">Not Verified</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
            {tabValue === 0 && hasActiveFilters && (
              <MDBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                <MDTypography variant="caption" color="text" sx={{ alignSelf: "center", mr: 1 }}>
                  Active filters:
                </MDTypography>
                {roleFilter !== "all" && (
                  <Chip
                    label={`Role: ${roleFilter}`}
                    onDelete={() => setRoleFilter("all")}
                    color="info"
                    size="small"
                    icon={<Icon>badge</Icon>}
                  />
                )}
                {statusFilter !== "all" && (
                  <Chip
                    label={`Status: ${statusFilter === "active" ? "Active" : "Inactive"}`}
                    onDelete={() => setStatusFilter("all")}
                    color={statusFilter === "active" ? "success" : "error"}
                    size="small"
                    icon={<Icon>{statusFilter === "active" ? "check_circle" : "block"}</Icon>}
                  />
                )}
              </MDBox>
            )}
            {tabValue === 1 && hasActiveClientFilters && (
              <MDBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                <MDTypography variant="caption" color="text" sx={{ alignSelf: "center", mr: 1 }}>
                  Active filters:
                </MDTypography>
                {clientStatusFilter !== "all" && (
                  <Chip
                    label={`Status: ${clientStatusFilter === "active" ? "Active" : "Inactive"}`}
                    onDelete={() => setClientStatusFilter("all")}
                    color={clientStatusFilter === "active" ? "success" : "error"}
                    size="small"
                  />
                )}
                {clientEmailVerifiedFilter !== "all" && (
                  <Chip
                    label={`Email: ${
                      clientEmailVerifiedFilter === "verified" ? "Verified" : "Not Verified"
                    }`}
                    onDelete={() => setClientEmailVerifiedFilter("all")}
                    color={clientEmailVerifiedFilter === "verified" ? "success" : "warning"}
                    size="small"
                  />
                )}
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Staff or Clients Table */}
        <Card>
          {tabValue === 0 &&
            (loading ? (
              <MDBox p={6} display="flex" justifyContent="center">
                <CircularProgress />
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                showTotalEntries={true}
                isSorted={false}
                noEndBorder
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                canSearch
              />
            ))}
          {tabValue === 1 &&
            (clientsLoading ? (
              <MDBox p={6} display="flex" justifyContent="center">
                <CircularProgress />
              </MDBox>
            ) : (
              <DataTable
                table={{ columns: clientColumns, rows: clientRows }}
                showTotalEntries={true}
                isSorted={false}
                noEndBorder
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                canSearch
              />
            ))}
        </Card>

        {/* Action Menu (Staff only) */}
        {tabValue === 0 && (
          <Menu
            id="user-menu"
            anchorReference="anchorPosition"
            anchorPosition={menuAnchorPosition ?? undefined}
            anchorEl={null}
            open={Boolean(menuAnchorPosition)}
            onClose={closeMenu}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            MenuListProps={{
              "aria-labelledby": "user-menu-button",
              sx: {
                py: 0.5,
              },
            }}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 180,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                const userId = selectedUser?._id || selectedUser?.id;
                if (userId) {
                  navigate(`/super-admin/users/profile/${userId}`);
                }
                closeMenu();
              }}
            >
              <Icon sx={{ mr: 1 }}>person</Icon> Profile
            </MenuItem>
            {selectedUser?.isActive ? (
              <MenuItem onClick={() => openActionDialog("deactivate")}>
                <Icon sx={{ mr: 1 }}>block</Icon> Deactivate
              </MenuItem>
            ) : (
              <MenuItem onClick={() => openActionDialog("activate")}>
                <Icon sx={{ mr: 1 }}>check_circle</Icon> Activate
              </MenuItem>
            )}
            <MenuItem onClick={() => openActionDialog("delete")} sx={{ color: "error.main" }}>
              <Icon sx={{ mr: 1 }}>delete</Icon> Delete
            </MenuItem>
          </Menu>
        )}

        {/* Action Confirmation Dialog (Staff only) */}
        {tabValue === 0 && (
          <Dialog
            open={actionDialog.open}
            onClose={() => {
              if (!submitting) {
                setActionDialog({ open: false, action: null });
                setSelectedUser(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {actionDialog.action === "delete" && "Confirm Deletion"}
              {actionDialog.action === "deactivate" && "Confirm Deactivation"}
              {actionDialog.action === "activate" && "Confirm Activation"}
            </DialogTitle>
            <DialogContent>
              <MDTypography variant="body2" color="text" mb={2}>
                {actionDialog.action === "delete" &&
                  "Are you sure you want to permanently delete this user? This action cannot be undone."}
                {actionDialog.action === "deactivate" &&
                  "Are you sure you want to deactivate this user? They will no longer be able to access the system."}
                {actionDialog.action === "activate" &&
                  "Are you sure you want to activate this user? They will be able to access the system again."}
              </MDTypography>
              {selectedUser && (
                <MDBox p={2} borderRadius="lg" bgColor="grey.100">
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {selectedUser.fullName}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {selectedUser.email}
                  </MDTypography>
                </MDBox>
              )}
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setActionDialog({ open: false, action: null });
                  setSelectedUser(null);
                }}
                disabled={submitting}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={confirmAction}
                variant="gradient"
                color={
                  actionDialog.action === "delete"
                    ? "error"
                    : actionDialog.action === "deactivate"
                    ? "warning"
                    : "success"
                }
                disabled={submitting}
              >
                {submitting
                  ? actionDialog.action === "delete"
                    ? "Deleting..."
                    : actionDialog.action === "deactivate"
                    ? "Deactivating..."
                    : "Activating..."
                  : actionDialog.action === "delete"
                  ? "Delete"
                  : actionDialog.action === "deactivate"
                  ? "Deactivate"
                  : "Activate"}
              </MDButton>
            </DialogActions>
          </Dialog>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersManagement;
